/* eslint-disable no-underscore-dangle */
import React, {
  useEffect, useState, useCallback, useRef,
} from 'react';
import {
  Table, Form, Button,
  Alert, Spinner, Badge,
} from 'react-bootstrap';
import Datetime from 'react-datetime';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import { Typeahead } from 'react-bootstrap-typeahead';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import { formatBookingDate, DEFAULT_TIMEZONE, localToUtc } from '../../../Utils/timezone';
import TimezoneSelect from '../../../Components/TimezoneSelect';
import { isSystemManagedStatus, systemManagedStatusLabel } from '../../../Utils/bookingStatus';
import setStateFromInput from '../../../Utils/setStateFromInput';
import extractFlightNumber from '../../../Utils/extractFlightNumber';
import { cashPortionTotal, hasOrphanCashPortion } from '../../../Utils/orderCash';
import { waitingTimesWarning } from '../../../Utils/freeWaiting';
import {
  isExpenseBooking, expenseCostGap, DEFAULT_EXPENSE_CURRENCY,
} from '../../../Utils/bookingType';
import { PICKUP_HUNGARY_PARTNER_ID } from '../../../config/config';
import AddressForm from './AddressForm';
import ItineraryStopsEditor from './ItineraryStopsEditor';
import LinkBookingsPanel from './LinkBookingsPanel';
import FlightService from '../../../Services/FlightService';
import BookingService from '../../../Services/BookingService';
import PricingService from '../../../Services/PricingService';
import ExperienceService from '../../../Services/ExperienceService';
import BookingServicesPicker from './BookingServicesPicker';
import MultiOperatorAssignment from './MultiOperatorAssignment';

dayjs.extend(utc);
dayjs.extend(tz);

const BOOKING_TYPES = [
  {
    value: 'transfer',
    label: 'Transfer',
    icon: 'fa-car',
    description: 'Point-to-point or multi-stop transfer',
  },
  {
    value: 'experience',
    label: 'Experience',
    icon: 'fa-map-marked-alt',
    description: 'Tour, activity, or guided experience',
  },
  {
    value: 'expense',
    label: 'Expense',
    icon: 'fa-receipt',
    // Says the two things the office gets wrong about this form: the name
    // field is a description, and the amount is not the price field.
    description: 'A cost that is not a trip — car rental, fuel, admin. '
      + 'Describe it in Description and put the amount on the operator.',
  },
];

function OrderInputForm({
  originalOrder,
  validationErrors,
  partners,
  setOriginalOrder,
  paymentTypes,
  orderStatuses,
  onPreview,
}) {
  const [order, setOrder] = useState(originalOrder);
  const [totalPassengers, setTotalPassengers] = useState(0);
  const [decoratedPartners, setDecoratedPartners] = useState([]);
  const [datetime, setDatetime] = useState(order.date_datetime
    ? formatBookingDate(order, 'date_datetime') : null);
  const [returnDatetime, setReturnDatetime] = useState(order.return_date_datetime
    ? formatBookingDate(order, 'return_date_datetime') : null);
  // Timezone for date/time input - defaults to Budapest, or use stored timezone
  const [bookingTimezone, setBookingTimezone] = useState(
    order.timezone || DEFAULT_TIMEZONE,
  );

  const decoratePartnerOption = (option) => {
    if (!option) return null;

    return {
      id: option.Id,
      label: `${option.name}`,
    };
  };

  const [selectedPartner, setSelectedPartner] = useState([]);

  // Flight validation state
  const [flightValidation, setFlightValidation] = useState(null);
  const [flightLoading, setFlightLoading] = useState(false);
  const [flightLookupTimeout, setFlightLookupTimeout] = useState(null);

  // Coordinate warning state - shown on first preview click, confirmed on second
  const [showCoordWarning, setShowCoordWarning] = useState(false);

  // Event booking state
  const [eventNames, setEventNames] = useState([]);
  const [isLoadingEventNames, setIsLoadingEventNames] = useState(false);

  // Vehicle categories state
  const [vehicleCategories, setVehicleCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);

  // Experience templates state
  const [experienceTemplates, setExperienceTemplates] = useState([]);
  const [selectedExperience, setSelectedExperience] = useState([]);
  const [isLoadingExperiences, setIsLoadingExperiences] = useState(false);

  // Price change notification state
  const [priceChangeNote, setPriceChangeNote] = useState(null);

  // Capacity warning - when passengers exceed vehicle capacity
  const capacityExceeded = order.category?.capacity > 0
    && totalPassengers > order.category.capacity;

  /**
   * An expense is a COST, not a journey, so this form collapses to the five
   * things the API actually asks of one — description (`full_name`), date,
   * partner, currency and status — plus a note. Everything a passenger or a
   * vehicle would need is hidden rather than left blank: a fuel receipt has
   * no pickup address, and leaving the field there is how one of the nine
   * fake bookings in production ended up with its expense breakdown typed
   * into `destination_from`.
   *
   * WHAT AN EXPENSE ADDS BACK: the cost itself. On a trip the operator is
   * assigned afterwards, through Assign Operator, because a booking is taken
   * before anybody has been found to run it. A cost record is the other way
   * round — who we are paying and how much IS the record, and the API refuses
   * a new expense that names neither. So the same `MultiOperatorAssignment`
   * the Assign Operator modal uses is rendered inline here, with the same
   * props, rather than a second picker that would have to re-learn the
   * two-group list and the driver-role guard.
   */
  const isExpense = isExpenseBooking(order);

  /**
   * What this expense is still missing, in the API's words, or null. Derived
   * rather than stored: it must be on screen while the fields are empty, not
   * only after a Preview click has been refused.
   */
  const costGap = expenseCostGap(order);

  // Check if an address is too vague (just city/region)
  const isAddressTooVague = (address) => {
    if (!address) return false;
    const parts = address.split(',').map((p) => p.trim());
    if (parts.length <= 2) return true;
    const firstPart = parts[0];
    const hasStreetNumber = /\d/.test(firstPart);
    const poiKeywords = /airport|hotel|station|terminal|restaurant|mall|center|centre|museum|hospital|university/i;
    const isPoi = poiKeywords.test(firstPart);
    return !hasStreetNumber && !isPoi;
  };

  const handleSubmit = () => {
    // Check for missing coordinates or vague addresses - show warning on first click.
    // An expense has no addresses to be missing, so the check is skipped
    // entirely: left in, `missingCoords` would be true on every expense and
    // the warning would demand a second Preview click to get past a journey
    // problem the record cannot have.
    const missingCoords = !isExpense
      && (!order.destination_from?.latitude || !order.destination_to?.latitude);
    const vagueFrom = !isExpense && order.destination_from?.latitude
      && isAddressTooVague(order.destination_from?.address);
    const vagueTo = !isExpense && order.destination_to?.latitude
      && isAddressTooVague(order.destination_to?.address);
    const hasIssues = missingCoords || vagueFrom || vagueTo;

    if (hasIssues && !showCoordWarning) {
      setShowCoordWarning(true);
      return;
    }

    // Partner and currency are filled in for the office below, because an
    // expense's answer to both is the same nearly every time. This one
    // cannot be: who the cost is settled against and how much is the point
    // of the record. Refuse here rather than let the API refuse it two
    // screens later — the Preview button is already disabled and says so,
    // and this is the backstop for reaching it any other way.
    if (costGap) return;

    // Convert local time to real UTC for storage using centralized utility
    order.date_datetime = localToUtc(datetime, bookingTimezone) || undefined;
    order.return_date_datetime = localToUtc(returnDatetime, bookingTimezone);
    // Store timezone alongside the dates
    order.timezone = bookingTimezone;

    setOriginalOrder(order);
    onPreview();
  };

  const recalculatePassengers = () => {
    let amount;
    if (order.booking_type === 'experience') {
      // For experiences: adults + children (no seat requirements)
      amount = Number(order.passengers.adults || 0)
        + Number(order.passengers.children || 0);
    } else {
      // For transfers: adults + seat counts
      amount = Number(order.passengers.adults || 0)
        + Number(order.passengers.infant_carrier || 0)
        + Number(order.passengers.booster_seat || 0)
        + Number(order.passengers.convertible_seat || 0);
    }

    setTotalPassengers(amount);

    if (amount !== order.passengers.amount) {
      setOrder((prevState) => ({ ...prevState, passengers: { ...prevState.passengers, amount } }));
    }
  };

  /**
   * Handle input change
   * @param {object} e
   * @returns void
   */
  const handleChange = (e) => {
    setStateFromInput(e, setOrder);
  };

  // Flight lookup function
  const lookupFlight = useCallback(async (flightNumber) => {
    if (!flightNumber || flightNumber.trim().length < 3) {
      setFlightValidation(null);
      return;
    }

    // Skip "no flight" type entries
    if (flightNumber.toLowerCase().includes('no flight')) {
      setFlightValidation(null);
      return;
    }

    // Extract just the flight code (e.g., "PC331 14:15" -> "PC331")
    const flightCode = extractFlightNumber(flightNumber);

    setFlightLoading(true);
    try {
      const validation = await FlightService.lookupFlight(
        flightCode,
        order.destination_from?.address || '',
        order.destination_to?.address || '',
        datetime ? dayjs(datetime).format('YYYY-MM-DD HH:mm') : '',
      );
      setFlightValidation(validation);
    } catch (err) {
      setFlightValidation({
        found: false,
        warnings: ['Flight lookup unavailable'],
        suggestions: [],
      });
    } finally {
      setFlightLoading(false);
    }
  }, [order.destination_from?.address, order.destination_to?.address, datetime]);

  // Handle flight number change with debounce
  const handleFlightNumberChange = (e) => {
    handleChange(e);

    // Clear previous timeout
    if (flightLookupTimeout) {
      clearTimeout(flightLookupTimeout);
    }

    // Debounce flight lookup
    const timeout = setTimeout(() => {
      lookupFlight(e.target.value);
    }, 800);
    setFlightLookupTimeout(timeout);
  };

  // Apply flight arrival time to pickup time
  // IMPORTANT: Extract time directly from ISO string to preserve destination timezone
  // Don't use dayjs() which would convert to browser's local timezone
  // Always use scheduled time (not estimated) for pickup planning
  const applyFlightArrivalTime = () => {
    if (flightValidation?.found && flightValidation?.flight?.arrival?.scheduled) {
      const arrivalTime = flightValidation.flight.arrival.scheduled;
      // Extract date and time parts from ISO string (e.g., "2025-12-08 19:10+01:00")
      const match = arrivalTime.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
      if (match) {
        // Use the time as-is from the API (destination local time)
        setDatetime(`${match[1]} ${match[2]}`);
      } else {
        // Fallback to original value if format doesn't match
        setDatetime(arrivalTime);
      }
    }
  };

  // Format flight time for display with timezone
  // IMPORTANT: Display time as-is from API (already in destination local time)
  // Don't let dayjs convert to browser's local timezone
  const formatFlightTime = (isoString, timezone) => {
    if (!isoString) return '';

    // Extract date and time parts from ISO string (e.g., "2025-12-08 19:10+01:00")
    // The time is already in the destination's local timezone
    const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) return isoString;

    const [, year, month, day, hour, minute] = match;
    // Create date object just for day-of-week formatting (use UTC to avoid TZ shifts)
    const dateForDay = new Date(Date.UTC(year, month - 1, day));
    const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dateForDay.getUTCDay()];
    const monthName = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][parseInt(month, 10) - 1];

    // Format: "Mon, 8 Dec 2025 19:10 (Budapest)"
    const formatted = `${dayOfWeek}, ${parseInt(day, 10)} ${monthName} ${year} ${hour}:${minute}`;
    const tzAbbrev = timezone ? ` (${timezone.split('/').pop()})` : '';
    return `${formatted}${tzAbbrev}`;
  };

  const setBillingDetailsFromPartner = (partnerId) => {
    const partnerBillingDetails = partners.find((partner) => partner.Id === partnerId);

    if (partnerBillingDetails) {
      const billingDetails = {};

      billingDetails.name = partnerBillingDetails.billing?.name || partnerBillingDetails.name;
      billingDetails.address = partnerBillingDetails.billing?.address || '';
      billingDetails.country = partnerBillingDetails.billing?.country || '';
      billingDetails.postcode = partnerBillingDetails.billing?.postcode || '';
      billingDetails.city = partnerBillingDetails.billing?.city || '';

      setOrder((prevState) => (
        { ...prevState, billing: { ...billingDetails } }
      ));
    }
  };

  const updatePartner = () => {
    if (order.partner_id && partners.length !== 0) {
      const decoratedPartner = decoratePartnerOption(
        partners.find((partner) => partner.Id === order.partner_id),
      );

      if (decoratedPartner) {
        setSelectedPartner([decoratedPartner]);
      }
    }
  };

  // Sync local order state when originalOrder changes (e.g., from AI extraction)
  // Use a ref to track the previous originalOrder to detect meaningful changes
  const prevOriginalOrderRef = useRef(originalOrder);
  useEffect(() => {
    const prevOrder = prevOriginalOrderRef.current;

    // Helper to strip destinations, programme, and estimates for comparison
    // We strip these since they're synced separately to avoid circular updates
    const stripFieldsForCompare = (o) => {
      if (!o) return {};
      return {
        ...o,
        destination_from: null,
        destination_to: null,
        programme: null,
        estimated_distance: null,
        estimated_duration: null,
      };
    };

    // Check if this is a meaningful change (not just destination/programme propagation)
    // We skip sync if only these fields changed (to avoid circular updates)
    const onlyPropagatedFieldsChanged = prevOrder
      && prevOrder._id === originalOrder._id
      && JSON.stringify(stripFieldsForCompare(prevOrder))
        === JSON.stringify(stripFieldsForCompare(originalOrder));

    prevOriginalOrderRef.current = originalOrder;

    // Skip if only propagated fields changed (destinations, programme stops)
    if (onlyPropagatedFieldsChanged) {
      return;
    }

    setOrder(originalOrder);
    // Also update datetime state if date changed
    if (originalOrder.date_datetime) {
      setDatetime(formatBookingDate(originalOrder, 'date_datetime'));
    }
    if (originalOrder.return_date_datetime) {
      setReturnDatetime(formatBookingDate(originalOrder, 'return_date_datetime'));
    }
    // Clear flight validation when order changes
    setFlightValidation(null);
  }, [originalOrder]);

  // Trigger flight lookup when flight number exists and datetime is set
  // This handles both initial load and when order changes
  useEffect(() => {
    // Only lookup if we have both flight number AND a date (required for accurate results)
    if (order.flight_number && order.flight_number.trim().length >= 3 && datetime) {
      lookupFlight(order.flight_number);
    }
  }, [order.flight_number, datetime, lookupFlight]);

  // Propagate destination coordinate changes back to parent for map display
  // Only triggers when coordinates change (not when address text changes)
  const initialRenderRef = useRef(true);
  useEffect(() => {
    // Skip on initial render
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Only update parent when coordinates change (for map display)
    setOriginalOrder((prev) => {
      // Check if coordinates actually changed
      const fromCoordsChanged = prev.destination_from?.latitude
          !== order.destination_from?.latitude
        || prev.destination_from?.longitude !== order.destination_from?.longitude;
      const toCoordsChanged = prev.destination_to?.latitude !== order.destination_to?.latitude
        || prev.destination_to?.longitude !== order.destination_to?.longitude;

      if (!fromCoordsChanged && !toCoordsChanged) {
        return prev; // No change, return same reference
      }

      return {
        ...prev,
        destination_from: { ...order.destination_from },
        destination_to: { ...order.destination_to },
      };
    });
  }, [
    order.destination_from?.latitude,
    order.destination_from?.longitude,
    order.destination_to?.latitude,
    order.destination_to?.longitude,
  ]);

  // Propagate programme.stops changes back to parent for map display
  // This allows the map to show intermediate stops in real-time
  const stopsInitialRenderRef = useRef(true);
  useEffect(() => {
    // Skip on initial render
    if (stopsInitialRenderRef.current) {
      stopsInitialRenderRef.current = false;
      return;
    }

    // Only update parent when stops with coordinates change
    setOriginalOrder((prev) => {
      const prevStopsKey = JSON.stringify(
        (prev.programme?.stops || []).map((s) => ({
          lat: s.latitude,
          lon: s.longitude,
          type: s.stop_type,
        })),
      );
      const newStopsKey = JSON.stringify(
        (order.programme?.stops || []).map((s) => ({
          lat: s.latitude,
          lon: s.longitude,
          type: s.stop_type,
        })),
      );

      if (prevStopsKey === newStopsKey) {
        return prev; // No change in stops with coordinates
      }

      return {
        ...prev,
        programme: {
          ...prev.programme,
          stops: order.programme?.stops || [],
        },
      };
    });
  }, [order.programme?.stops]);

  useEffect(() => {
    setTimeout(recalculatePassengers);

    if (order.partner_id
            && !(
              order.billing.name
              || order.billing.address
              || order.billing.country
              || order.billing.postcode
              || order.billing.ciy)) {
      setTimeout(() => setBillingDetailsFromPartner(order.partner_id));
    }

    updatePartner();
  }, [order]);

  useEffect(() => {
    const partnersTypeAhead = [];

    partners.forEach((partnerRaw) => {
      partnersTypeAhead.push(
        decoratePartnerOption(partnerRaw),
      );
    });

    setDecoratedPartners(partnersTypeAhead);
    updatePartner();
  }, [partners]);

  /**
   * THE TWO FIELDS AN EXPENSE SHOULD NEVER HAVE TO BE ASKED FOR.
   *
   * `partner_id` and `currency` are required of every booking, and a new
   * order form opens with neither. On a transfer that is right: both are
   * commercial facts about the sale. On a cost record they are the same
   * answer nearly every time — we bear it, in forints — and leaving them
   * empty is two of the three reasons the API refused every expense the
   * office tried to save.
   *
   * Only ever FILLS A BLANK. Editing an existing expense that is in EUR, or
   * one borne by a partner, must not be quietly rewritten to the house
   * defaults, and switching the type back to Transfer leaves what was
   * chosen rather than reverting it.
   */
  useEffect(() => {
    if (!isExpense) return;

    setOrder((prev) => {
      const next = { ...prev };
      if (!prev.partner_id) next.partner_id = PICKUP_HUNGARY_PARTNER_ID;
      if (!prev.currency) next.currency = DEFAULT_EXPENSE_CURRENCY;
      return next.partner_id === prev.partner_id && next.currency === prev.currency
        ? prev
        : next;
    });
  }, [isExpense]);

  useEffect(() => {
    let partnerId;

    if (Array.isArray(selectedPartner) && selectedPartner.length === 1) {
      partnerId = selectedPartner[0].id;

      if (order.partner_id !== partnerId) {
        setOrder((prev) => ({
          ...prev,
          partner_id: partnerId,
        }));
      }
    }
  }, [selectedPartner]);

  // Fetch event names when event booking mode is enabled
  useEffect(() => {
    const fetchEventNames = async () => {
      if (!order.is_event_booking) return;
      setIsLoadingEventNames(true);
      try {
        const names = await BookingService.getEventNames();
        // Include current event name if it exists and is not already in the list
        if (order.event_name && !names.includes(order.event_name)) {
          names.push(order.event_name);
          names.sort();
        }
        setEventNames(names);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching event names:', err);
      } finally {
        setIsLoadingEventNames(false);
      }
    };
    fetchEventNames();
  }, [order.is_event_booking, order.event_name]);

  // Fetch vehicle categories on mount
  useEffect(() => {
    const fetchVehicleCategories = async () => {
      try {
        const response = await PricingService.fetchVehicleCategories();
        const categories = response?.data?.data || [];
        // Filter to only active categories and map to typeahead format
        const activeCategories = categories
          .filter((c) => c.active === 1 || c.active === true)
          .map((c) => ({
            id: c.Id,
            label: c.name,
            capacity: c.capacity,
            max_luggage: c.max_luggage,
          }));
        setVehicleCategories(activeCategories);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching vehicle categories:', err);
      }
    };
    fetchVehicleCategories();
  }, []);

  // Fetch experience/programme templates for any booking type
  useEffect(() => {
    const fetchExperienceTemplates = async () => {
      setIsLoadingExperiences(true);
      try {
        const response = await ExperienceService.fetchExperiences();
        const experiences = response?.data?.data || [];
        // Filter to only active experiences and map to typeahead format
        const activeExperiences = experiences
          .filter((e) => e.active === 1 || e.active === true)
          .map((e) => ({
            id: e.Id,
            label: e.name,
            duration_hours: e.duration_hours,
            itinerary_template: e.itinerary_template,
            required_roles: e.required_roles,
            default_pricing: e.default_pricing,
            description: e.description,
            category: e.category,
            starting_point: e.starting_point,
            ending_point: e.ending_point,
            important_information: e.important_information,
            cancellation_policy: e.cancellation_policy,
          }));
        setExperienceTemplates(activeExperiences);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching experiences:', err);
      } finally {
        setIsLoadingExperiences(false);
      }
    };
    fetchExperienceTemplates();
  }, []);

  // Set initial selected experience when editing existing order
  useEffect(() => {
    if (order.programme?.experience_id && experienceTemplates.length > 0) {
      const exp = experienceTemplates.find((e) => e.id === order.programme.experience_id);
      if (exp && selectedExperience.length === 0) {
        setSelectedExperience([exp]);
      }
    }
  }, [order.programme?.experience_id, experienceTemplates]);

  // Apply experience template when selected
  const applyExperienceTemplate = (experience) => {
    if (!experience) return;

    // Convert itinerary_template to programme.stops format
    const stops = (experience.itinerary_template || []).map((item) => ({
      order: item.order,
      address: item.address || '',
      latitude: item.latitude,
      longitude: item.longitude,
      duration_minutes: item.duration_minutes,
      stop_type: item.stop_type || 'activity',
      notes: item.description || '',
    }));

    // Build destination_from from starting_point if set
    const destinationFrom = experience.starting_point
      && (experience.starting_point.address || experience.starting_point.latitude)
      ? {
        address: experience.starting_point.address || '',
        latitude: experience.starting_point.latitude,
        longitude: experience.starting_point.longitude,
      }
      : undefined;

    // Build destination_to from ending_point if set
    const destinationTo = experience.ending_point
      && (experience.ending_point.address || experience.ending_point.latitude)
      ? {
        address: experience.ending_point.address || '',
        latitude: experience.ending_point.latitude,
        longitude: experience.ending_point.longitude,
      }
      : undefined;

    // Calculate price based on pricing_type
    let calculatedPrice = 0;
    let priceCurrency = order.currency || 'EUR';
    const pricingType = experience.default_pricing?.pricing_type || 'per_group';
    const numPassengers = Number(order.passengers?.amount) || Number(order.passengers?.adults) || 1;

    if (pricingType === 'per_person' && experience.default_pricing?.price) {
      // Per person pricing - multiply adults by adult price, children by child price
      const adultPrice = experience.default_pricing.price;
      const childPrice = experience.default_pricing.child_price || 0;
      priceCurrency = experience.default_pricing.currency || 'EUR';
      const numAdults = Number(order.passengers?.adults) || 1;
      const numChildren = Number(order.passengers?.children) || 0;
      calculatedPrice = (adultPrice * numAdults) + (childPrice * numChildren);
    } else if (pricingType === 'per_group' && experience.default_pricing?.price) {
      // Per group - flat rate
      calculatedPrice = experience.default_pricing.price;
      priceCurrency = experience.default_pricing.currency || 'EUR';
    } else if (pricingType === 'passenger_tiers') {
      // Passenger tiers - find matching tier based on total passengers
      const tiers = experience.default_pricing?.passenger_tiers || [];
      const matchingTier = tiers.find((tier) => {
        const min = tier.min_passengers || 1;
        const max = tier.max_passengers || 0; // 0 = unlimited
        return numPassengers >= min && (max === 0 || numPassengers <= max);
      });
      if (matchingTier) {
        calculatedPrice = priceCurrency === 'HUF'
          ? (matchingTier.price_huf || matchingTier.price_eur)
          : matchingTier.price_eur;
      }
    } else if (pricingType === 'vehicle_category') {
      // Vehicle category pricing - price depends on selected vehicle category
      // Price will be set when category is selected, not here
      calculatedPrice = 0; // Will be updated when category is selected
    }

    setOrder((prev) => ({
      ...prev,
      // Copy starting_point to destination_from if set
      ...(destinationFrom && { destination_from: destinationFrom }),
      // Copy ending_point to destination_to if set
      ...(destinationTo && { destination_to: destinationTo }),
      // Apply calculated price if experience has default pricing
      ...(calculatedPrice > 0 && {
        original_price: calculatedPrice,
        price: calculatedPrice,
        currency: priceCurrency,
      }),
      // Copy important_information from experience if set
      ...(experience.important_information && {
        important_information: experience.important_information,
      }),
      // Copy cancellation_policy from experience if set
      ...(experience.cancellation_policy && {
        cancellation_policy: experience.cancellation_policy,
      }),
      programme: {
        ...prev.programme,
        enabled: true,
        experience_id: experience.id,
        name: experience.label,
        total_hours: experience.duration_hours || prev.programme.total_hours,
        itinerary: experience.description || prev.programme.itinerary,
        stops,
      },
    }));
  };

  // Recalculate experience price when passenger count changes
  useEffect(() => {
    if (selectedExperience.length > 0) {
      const experience = selectedExperience[0];
      const pricingType = experience.default_pricing?.pricing_type || 'per_group';
      const numPassengers = Number(order.passengers?.amount)
        || Number(order.passengers?.adults) || 1;
      const priceCurrency = order.currency || 'EUR';

      let calculatedPrice = null;

      if (pricingType === 'per_person' && experience.default_pricing?.price) {
        // Per person pricing - multiply adults by adult price, children by child price
        const adultPrice = experience.default_pricing.price;
        const childPrice = experience.default_pricing.child_price || 0;
        const numAdults = Number(order.passengers?.adults) || 1;
        const numChildren = Number(order.passengers?.children) || 0;
        calculatedPrice = (adultPrice * numAdults) + (childPrice * numChildren);
      } else if (pricingType === 'passenger_tiers') {
        // Passenger tiers - find matching tier based on total passengers
        const tiers = experience.default_pricing?.passenger_tiers || [];
        const matchingTier = tiers.find((tier) => {
          const min = tier.min_passengers || 1;
          const max = tier.max_passengers || 0; // 0 = unlimited
          return numPassengers >= min && (max === 0 || numPassengers <= max);
        });
        if (matchingTier) {
          calculatedPrice = priceCurrency === 'HUF'
            ? (matchingTier.price_huf || matchingTier.price_eur)
            : matchingTier.price_eur;
        }
      }

      // Only update if price actually changed and we calculated a new price
      if (calculatedPrice !== null && order.original_price !== calculatedPrice) {
        const oldPrice = order.original_price || 0;
        setOrder((prev) => ({
          ...prev,
          original_price: calculatedPrice,
          price: calculatedPrice,
        }));
        // Show price change note
        if (oldPrice > 0) {
          setPriceChangeNote({
            from: oldPrice,
            to: calculatedPrice,
            currency: priceCurrency,
          });
          // Auto-hide after 5 seconds
          setTimeout(() => setPriceChangeNote(null), 5000);
        }
      }
    }
  }, [
    order.passengers?.adults,
    order.passengers?.children,
    order.passengers?.amount,
    order.currency,
    selectedExperience,
  ]);

  // Set initial selected category when editing existing order
  useEffect(() => {
    if (order.category?.name && vehicleCategories.length > 0) {
      const category = vehicleCategories.find((c) => c.label === order.category.name);
      if (category && selectedCategory.length === 0) {
        setSelectedCategory([category]);
      }
    }
  }, [order.category?.name, vehicleCategories]);

  // Update order when vehicle category is selected
  useEffect(() => {
    if (Array.isArray(selectedCategory) && selectedCategory.length === 1) {
      const category = selectedCategory[0];
      const updates = {};

      // Update category info if changed
      if (order.category?.name !== category.label) {
        updates.category = {
          ...order.category,
          name: category.label,
          capacity: category.capacity,
          max_luggage: category.max_luggage,
        };
      }

      // If experience has vehicle_category pricing, update price based on selected category
      if (selectedExperience.length > 0) {
        const experience = selectedExperience[0];
        if (experience.default_pricing?.pricing_type === 'vehicle_category') {
          const categoryPrices = experience.default_pricing?.vehicle_category_prices || [];
          const matchingPrice = categoryPrices.find(
            (p) => p.vehicle_category_id === category.id,
          );
          if (matchingPrice) {
            const priceCurrency = order.currency || 'EUR';
            const calculatedPrice = priceCurrency === 'HUF'
              ? (matchingPrice.price_huf || matchingPrice.price_eur)
              : matchingPrice.price_eur;
            updates.original_price = calculatedPrice;
            updates.price = calculatedPrice;
          }
        }
      }

      // Apply updates if any
      if (Object.keys(updates).length > 0) {
        setOrder((prev) => ({ ...prev, ...updates }));
      }
    }
  }, [selectedCategory, selectedExperience, order.currency]);

  // Passenger manifest helpers
  const addPassenger = () => {
    const newManifest = [...(order.passenger_manifest || []), { name: '', flight_number: '', notes: '' }];
    setOrder((prev) => ({ ...prev, passenger_manifest: newManifest }));
  };

  const removePassenger = (index) => {
    const newManifest = (order.passenger_manifest || []).filter((item, i) => i !== index);
    setOrder((prev) => ({ ...prev, passenger_manifest: newManifest }));
  };

  const updatePassenger = (index, field, value) => {
    const newManifest = [...(order.passenger_manifest || [])];
    newManifest[index] = { ...newManifest[index], [field]: value };
    setOrder((prev) => ({ ...prev, passenger_manifest: newManifest }));
  };

  // Toggle event booking mode
  const toggleEventBooking = () => {
    const newValue = !order.is_event_booking;
    setOrder((prev) => ({
      ...prev,
      is_event_booking: newValue,
      // Initialize passenger_manifest if enabling event mode
      passenger_manifest: newValue ? (prev.passenger_manifest || []) : prev.passenger_manifest,
    }));
  };

  return (
    <>
      {/* Linked Bookings - at the top for visibility. Linking is for legs of
          one journey (outbound/return, a group travelling together); a cost
          record is not a leg of anything. */}
      {!isExpense && (
      <LinkBookingsPanel
        linkedBookingIds={order.linked_booking_ids || []}
        onLinkedBookingsChange={(ids) => setOrder((prev) => ({
          ...prev,
          linked_booking_ids: ids,
        }))}
      />
      )}

      <Table striped className="order-input-form-table">
        <tbody>
          {/* Partner Information */}
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>Partner Information</th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Partner
              <i className="fa fa-asterisk text-danger bg-body-secondary" />
            </td>
            <td>
              <Form.Group controlId="partner_id">
                <Typeahead
                  placeholder="Select a partner"
                  id="partner_id"
                  onChange={setSelectedPartner}
                  options={decoratedPartners}
                  selected={selectedPartner}
                  positionFixed
                />
                {/* <Form.Select
                className="bg-warning-subtle"
                value={order.partner_id}
                onChange={handleChange}
              >
                <option value="">Select a partner</option>
                {partners.map((partner) => (
                  <option key={partner.Id} value={partner.Id}>{partner.name}</option>
                ))}
              </Form.Select> */}
                <FormControlFeedback errors={validationErrors} field="partner_id" />
                {/* A warning, never a block: plenty of bookings legitimately
                    run on the house numbers, and a hard stop here would
                    leave the office unable to take the booking at all. It
                    says what will actually happen rather than that a box is
                    empty — and stays quiet for a PH-direct booking, where
                    the defaults are simply correct. */}
                {waitingTimesWarning(partners.find((p) => p.Id === order.partner_id)) && (
                  <div
                    className="alert alert-warning py-2 px-3 mt-2 mb-0 small"
                    data-testid="partner-waiting-times-warning"
                  >
                    <i className="fa fa-hourglass-half me-2" />
                    {waitingTimesWarning(partners.find((p) => p.Id === order.partner_id))}
                  </div>
                )}
              </Form.Group>
            </td>
          </tr>
          <tr>
            <td className="bg-body-secondary">Partner Booking Reference</td>
            <td>
              <Form.Group controlId="partner_booking_reference">
                <Form.Control
                  type="text"
                  value={order.partner_booking_reference}
                  onChange={handleChange}
                />
                <FormControlFeedback errors={validationErrors} field="partner_booking_reference" />
              </Form.Group>
            </td>
          </tr>

          {/* Booking Type */}
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>Booking Type</th>
          </tr>
          <tr>
            <td className="bg-body-secondary">Type</td>
            <td className="p-2">
              <div className="d-flex gap-2">
                {BOOKING_TYPES.map((type) => (
                  <Button
                    key={type.value}
                    size="sm"
                    variant={order.booking_type === type.value ? 'primary' : 'outline-primary'}
                    onClick={() => handleChange({ target: { id: 'booking_type', value: type.value } })}
                    title={type.description}
                  >
                    <i className={`fa ${type.icon} me-1`} />
                    {type.label}
                  </Button>
                ))}
              </div>
              <small className="text-muted d-block mt-1">
                {BOOKING_TYPES.find((t) => t.value === order.booking_type)?.description
                  || 'Select a booking type'}
              </small>
            </td>
          </tr>

          {/* A template pre-fills a PROGRAMME, and an event groups TRIPS.
              Neither has anything to pre-fill or group on a cost record, so
              both sections go together. */}
          {!isExpense && (
          <>
            {/* Programme/Experience Template Selector */}
            <tr>
              <td className="bg-body-secondary">
                {order.booking_type === 'experience' ? 'Experience Template' : 'Programme Template'}
              </td>
              <td>
                <Form.Group controlId="experience_template">
                  <div className="d-flex gap-2">
                    <Typeahead
                      id="experience_template_typeahead"
                      placeholder={isLoadingExperiences
                        ? 'Loading templates...'
                        : `Select a ${order.booking_type === 'experience' ? 'experience' : 'programme'} template...`}
                      options={experienceTemplates}
                      selected={selectedExperience}
                      onChange={(selected) => {
                        setSelectedExperience(selected);
                        if (selected.length > 0) {
                          applyExperienceTemplate(selected[0]);
                        }
                      }}
                      disabled={isLoadingExperiences}
                      className="flex-grow-1"
                      positionFixed
                      renderMenuItemChildren={(option) => (
                        <div>
                          <strong>{option.label}</strong>
                          <div className="small text-muted">
                            {option.duration_hours}
                            h -
                            {' '}
                            {option.category}
                            {option.required_roles?.length > 0 && (
                            <span className="ms-2">
                              (
                              {option.required_roles.join(', ')}
                              )
                            </span>
                            )}
                            {/* Display pricing info based on type */}
                            {option.default_pricing?.pricing_type === 'per_person'
                            && option.default_pricing?.price > 0 && (
                            <span className="ms-2 text-success">
                              {option.default_pricing.price}
                              {' '}
                              {option.default_pricing.currency}
                              /person
                            </span>
                            )}
                            {option.default_pricing?.pricing_type === 'per_group'
                            && option.default_pricing?.price > 0 && (
                            <span className="ms-2 text-success">
                              {option.default_pricing.price}
                              {' '}
                              {option.default_pricing.currency}
                              /group
                            </span>
                            )}
                            {option.default_pricing?.pricing_type === 'passenger_tiers' && (
                            <span className="ms-2 text-info">
                              <i className="fa fa-users me-1" />
                              Tiered pricing
                            </span>
                            )}
                            {option.default_pricing?.pricing_type === 'vehicle_category' && (
                            <span className="ms-2 text-warning">
                              <i className="fa fa-car me-1" />
                              By vehicle
                            </span>
                            )}
                          </div>
                        </div>
                      )}
                    />
                    {isLoadingExperiences && <Spinner size="sm" className="align-self-center" />}
                  </div>
                  <Form.Text className="text-muted">
                    <i className="fa fa-info-circle me-1" />
                    Optional: Select a template to pre-fill programme details
                  </Form.Text>
                </Form.Group>
              </td>
            </tr>

            {/* Event Details */}
            <tr>
              <th className="bg-dark-subtle" colSpan={2}>Event Details</th>
            </tr>
            <tr>
              <td className="bg-body-secondary">Is it an event booking?</td>
              <td className="p-2">
                <Button size="sm" variant="primary" onClick={toggleEventBooking}>
                  Switch Event Mode
                </Button>
                {order.is_event_booking && (
                <Badge bg="info" className="ms-2">Event Booking Enabled</Badge>
                )}
              </td>
            </tr>

            {order.is_event_booking && (
            <>
              <tr>
                <td className="bg-body-secondary">Event Name</td>
                <td>
                  <Form.Group controlId="event_name">
                    <div className="d-flex gap-2">
                      <Typeahead
                        id="event_name_typeahead"
                        allowNew
                        newSelectionPrefix="Create new event: "
                        placeholder={isLoadingEventNames ? 'Loading events...' : 'Select or create an event...'}
                        options={eventNames}
                        selected={order.event_name ? [order.event_name] : []}
                        onChange={(selected) => {
                          let value = '';
                          if (selected.length > 0) {
                            const item = selected[0];
                            value = typeof item === 'string' ? item : (item.label || item);
                          }
                          // Auto-enable is_event_booking when event_name is set
                          setOrder((prev) => ({
                            ...prev,
                            event_name: value,
                            is_event_booking: value ? true : prev.is_event_booking,
                          }));
                        }}
                        disabled={isLoadingEventNames}
                        className="flex-grow-1"
                        positionFixed
                      />
                      {isLoadingEventNames && <Spinner size="sm" className="align-self-center" />}
                    </div>
                    <FormControlFeedback errors={validationErrors} field="event_name" />
                  </Form.Group>
                </td>
              </tr>
              <tr>
                <td className="bg-body-secondary">Event Group</td>
                <td>
                  <Form.Group controlId="event_group">
                    <Form.Control
                      type="text"
                      value={order.event_group || ''}
                      onChange={handleChange}
                      placeholder="e.g., 14:30 BUD Arrival, Hotel Marriott Pickup"
                    />
                    <Form.Text className="text-muted">
                      <i className="fa fa-info-circle me-1" />
                      Used to group passengers with same pickup time/location
                    </Form.Text>
                    <FormControlFeedback errors={validationErrors} field="event_group" />
                  </Form.Group>
                </td>
              </tr>

              {/* Passenger Manifest */}
              <tr>
                <td className="bg-body-secondary" style={{ verticalAlign: 'top' }}>
                  Passenger Manifest
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={addPassenger}
                    >
                      <i className="fa fa-plus me-1" />
                      Add Passenger
                    </Button>
                  </div>
                </td>
                <td>
                  {(!order.passenger_manifest || order.passenger_manifest.length === 0) ? (
                    <div className="text-muted py-2">
                      <i className="fa fa-users me-2" />
                      No passengers added. Click &quot;Add Passenger&quot; to add passengers.
                    </div>
                  ) : (
                    <div className="passenger-manifest-list">
                      {order.passenger_manifest.map((passenger, index) => (
                        <div
                          key={`passenger-${index + 1}`}
                          className="d-flex gap-2 mb-2 p-2 border rounded bg-light"
                        >
                          <div className="flex-grow-1">
                            <div className="d-flex gap-2 mb-1">
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Passenger name"
                                value={passenger.name || ''}
                                onChange={(e) => updatePassenger(index, 'name', e.target.value)}
                                className="flex-grow-1"
                              />
                              <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Flight (opt.)"
                                value={passenger.flight_number || ''}
                                onChange={(e) => updatePassenger(index, 'flight_number', e.target.value)}
                                style={{ maxWidth: '120px' }}
                              />
                            </div>
                            <Form.Control
                              size="sm"
                              type="text"
                              placeholder="Notes (optional)"
                              value={passenger.notes || ''}
                              onChange={(e) => updatePassenger(index, 'notes', e.target.value)}
                            />
                          </div>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => removePassenger(index)}
                            title="Remove passenger"
                          >
                            <i className="fa fa-trash" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <FormControlFeedback errors={validationErrors} field="passenger_manifest" />
                </td>
              </tr>
            </>
            )}
          </>
          )}

          {/* Passenger Information — on an expense this is the ONE field
              that survives, because `full_name` is the API's description
              field for a cost. It is relabelled rather than replaced: every
              list, search and CSV in the app already renders `full_name`,
              so writing the description anywhere else would make expenses
              invisible in all of them. */}
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>
              {isExpense ? 'Expense' : 'Passenger Information'}
            </th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              {isExpense ? 'Description' : 'Full Name'}
              <i className="fa fa-asterisk text-danger bg-body-secondary" />
            </td>
            <td>
              <Form.Group controlId="full_name">
                <Form.Control
                  className="bg-warning-subtle"
                  type="text"
                  value={order.full_name}
                  onChange={handleChange}
                  placeholder={isExpense ? 'e.g. Fuel — Shell M1, week 42' : undefined}
                />
                <FormControlFeedback errors={validationErrors} field="full_name" />
                {isExpense && (
                  <Form.Text className="text-muted" data-testid="expense-description-help">
                    What the money was spent on. This is what the orders list,
                    the search and the CSV will show.
                  </Form.Text>
                )}
              </Form.Group>
            </td>
          </tr>
          {!isExpense && (
          <>
            <tr>
              <td className="bg-body-secondary">
                Email Address
              </td>
              <td>
                <Form.Group controlId="email_address">
                  <Form.Control
                    type="email"
                    value={order.email_address}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="email_address" />
                </Form.Group>
              </td>
            </tr>
            <tr>
              <td className="bg-body-secondary">
                Phone Number
                <i className="fa fa-asterisk text-danger bg-body-secondary" />
              </td>
              <td>
                <Form.Group controlId="phone_number">
                  <Form.Control
                    className="bg-warning-subtle"
                    type="text"
                    value={order.phone_number}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="phone_number" />
                </Form.Group>
              </td>
            </tr>
          </>
          )}

          {/* Travel Information — the date survives on an expense (the API
              requires it), and it may be in the PAST: costs are recorded
              after the money has gone. */}
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>
              {isExpense ? 'When' : 'Travel Information'}
            </th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Date
              <i className="fa fa-asterisk text-danger bg-body-secondary" />
            </td>
            <td>
              <Form.Group controlId="date_datetime">
                <Datetime
                  inputProps={{ className: 'bg-warning-subtle form-control' }}
                  dateFormat="YYYY-MM-DD"
                  timeFormat="HH:mm"
                  value={datetime}
                  onChange={(value) => setDatetime(value)}
                />
                <FormControlFeedback errors={validationErrors} field="date_datetime" />
                {isExpense && (
                  <Form.Text className="text-muted" data-testid="expense-date-help">
                    The date the cost was incurred. A past date is expected.
                  </Form.Text>
                )}
                <TimezoneSelect
                  value={bookingTimezone}
                  onChange={setBookingTimezone}
                  compact
                  showIcon
                />
              </Form.Group>
            </td>
          </tr>
          {returnDatetime && (
          <tr>
            <td className="bg-body-secondary">
              Return Date
              {' '}
              <small className="text-muted">(Legacy)</small>
            </td>
            <td>
              <Form.Group controlId="return_date_datetime">
                <Datetime
                  dateFormat="YYYY-MM-DD"
                  timeFormat="HH:mm"
                  value={returnDatetime}
                  onChange={(value) => setReturnDatetime(value)}
                  inputProps={{ disabled: true, className: 'form-control bg-secondary-subtle' }}
                />
                <Form.Text className="text-muted">
                  This is a legacy field and cannot be edited
                </Form.Text>
                <FormControlFeedback errors={validationErrors} field="return_date_datetime" />
              </Form.Group>
            </td>
          </tr>
          )}

          {/* A cost record has no route. This whole section is where one of
              the nine fake expense bookings in production ended up storing
              its cost breakdown, for want of anywhere else to type. */}
          {!isExpense && (
          <>
            {/* Route & Itinerary - Combined section for Pickup, Drop-off and Programme */}
            <tr>
              <th className="bg-dark-subtle" colSpan={2}>
                <i className="fa fa-route me-2" />
                Route & Itinerary
                {order.booking_type === 'experience' && (
                <Badge bg="info" className="ms-2">Experience</Badge>
                )}
              </th>
            </tr>

            {/* Pickup / Experience Start Address */}
            <tr>
              <td className="bg-body-secondary">
                {order.booking_type === 'experience' ? 'Experience Starts From' : 'Pickup'}
                <i className="fa fa-asterisk text-danger ms-1" style={{ fontSize: '0.6em' }} />
              </td>
              <td>
                <Form.Group>
                  <AddressForm
                    handleChange={handleChange}
                    input={order}
                    fieldKey="destination_from.address"
                    label={order.booking_type === 'experience' ? 'Experience Starts From' : 'Pickup'}
                    validationErrors={validationErrors}
                    showCoordWarning={showCoordWarning}
                    inline
                  />
                </Form.Group>
              </td>
            </tr>

            {/* Drop-off / Experience End Address */}
            <tr>
              <td className="bg-body-secondary">
                {order.booking_type === 'experience' ? 'Experience Ends At' : 'Drop-off'}
                <i className="fa fa-asterisk text-danger ms-1" style={{ fontSize: '0.6em' }} />
              </td>
              <td>
                <Form.Group>
                  <AddressForm
                    handleChange={handleChange}
                    input={order}
                    fieldKey="destination_to.address"
                    label={order.booking_type === 'experience' ? 'Experience Ends At' : 'Drop-off'}
                    validationErrors={validationErrors}
                    showCoordWarning={showCoordWarning}
                    inline
                  />
                </Form.Group>
              </td>
            </tr>

            {/* Programme Toggle - only for transfers (experiences always
                have programme enabled) */}
            {order.booking_type !== 'experience' && (
            <tr>
              <td className="bg-body-secondary">Multi-stop / Programme</td>
              <td className="p-2">
                <Form.Check
                  type="switch"
                  id="programme-enabled-switch"
                  label={order.programme.enabled ? 'Enabled - Add intermediate stops' : 'Disabled'}
                  checked={order.programme.enabled}
                  onChange={() => handleChange({ target: { id: 'programme.enabled', value: !order.programme.enabled } })}
                />
              </td>
            </tr>
            )}

            {/* Programme Details - Always shown for experiences, toggled for transfers */}
            {(order.booking_type === 'experience' || order.programme.enabled) && (
            <>
              <tr>
                <td className="bg-body-secondary">Total Duration (h)</td>
                <td>
                  <Form.Group controlId="programme.total_hours">
                    <Form.Control
                      type="number"
                      min={0}
                      step={0.1}
                      value={order.programme.total_hours}
                      onChange={handleChange}
                    />
                    <FormControlFeedback errors={validationErrors} field="programme.total_hours" />
                  </Form.Group>
                </td>
              </tr>

              <tr>
                <td className="bg-body-secondary">Programme Name</td>
                <td>
                  <Form.Group controlId="programme.name">
                    <Form.Control
                      type="text"
                      value={order.programme.name}
                      onChange={handleChange}
                    />
                    <FormControlFeedback errors={validationErrors} field="programme.name" />
                  </Form.Group>
                </td>
              </tr>

              <tr>
                <td className="bg-body-secondary">Itinerary (Text)</td>
                <td>
                  <Form.Group controlId="programme.itinerary">
                    <Form.Control
                      as="textarea"
                      value={order.programme.itinerary}
                      onChange={handleChange}
                      placeholder="Free-text description of the itinerary..."
                    />
                    <FormControlFeedback errors={validationErrors} field="programme.itinerary" />
                  </Form.Group>
                </td>
              </tr>

              <tr>
                <td colSpan={2} className="p-3">
                  <ItineraryStopsEditor
                    stops={order.programme?.stops || []}
                    onChange={(stops) => setOrder((prev) => ({
                      ...prev,
                      programme: { ...prev.programme, stops },
                    }))}
                    startDateTime={datetime}
                    timezone={bookingTimezone}
                    fromCoords={
                    order.destination_from?.latitude && order.destination_from?.longitude
                      ? {
                        lat: order.destination_from.latitude,
                        lon: order.destination_from.longitude,
                      }
                      : null
                  }
                    toCoords={
                    order.destination_to?.latitude && order.destination_to?.longitude
                      ? {
                        lat: order.destination_to.latitude,
                        lon: order.destination_to.longitude,
                      }
                      : null
                  }
                  />
                </td>
              </tr>
            </>
            )}
          </>
          )}

          {/* Flight and Comments - Flight only for transfers. An expense has
              no flight either, and `booking_type !== 'experience'` would let
              one through — hence the explicit expense test rather than
              another inverted comparison against the other type. */}
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>
              {(order.booking_type === 'experience' || isExpense) ? 'Comments' : 'Flight and Comments'}
            </th>
          </tr>
          {!isExpense && order.booking_type !== 'experience' && (
          <tr>
            <td className="bg-body-secondary">Flight Number</td>
            <td>
              <Form.Group controlId="flight_number">
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="text"
                    value={order.flight_number}
                    onChange={handleFlightNumberChange}
                    style={{ flex: 1 }}
                  />
                  {flightLoading && <Spinner size="sm" />}
                </div>
                <FormControlFeedback errors={validationErrors} field="flight_number" />

                {/* Flight Validation Results */}
                {flightValidation && (
                <div className="mt-2">
                  {/* Warnings */}
                  {flightValidation.warnings?.length > 0 && (
                    <Alert variant="danger" className="py-2 mb-2">
                      <i className="fa fa-exclamation-circle me-2" />
                      {flightValidation.warnings.map((w) => (
                        <div key={w}>{w}</div>
                      ))}
                    </Alert>
                  )}

                  {/* Flight Found - Show Details */}
                  {flightValidation.found && flightValidation.flight && (
                    <div className="p-2 bg-light rounded border">
                      {/* Airline and Status */}
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          <img
                            src={`https://pics.avs.io/100/100/${
                              flightValidation.flight.flightNumber.substring(0, 2)
                            }.png`}
                            alt=""
                            style={{ width: 50, height: 50, marginRight: 12 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div>
                            <strong>{flightValidation.flight.airline}</strong>
                            <span className="text-muted ms-2">
                              {flightValidation.flight.flightNumber}
                            </span>
                          </div>
                        </div>
                        <Badge
                          bg={flightValidation.flight.status === 'Unknown'
                            ? 'secondary' : 'success'}
                        >
                          {flightValidation.flight.status}
                        </Badge>
                      </div>
                      {/* Route */}
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                        <div>
                          <small className="text-muted d-block">From</small>
                          <strong>{flightValidation.flight.departure.airport}</strong>
                          {' '}
                          <span className="text-muted">
                            (
                            {flightValidation.flight.departure.iata}
                            )
                          </span>
                        </div>
                        <i className="fa fa-plane text-primary" />
                        <div>
                          <small className="text-muted d-block">To</small>
                          <strong>{flightValidation.flight.arrival.airport}</strong>
                          {' '}
                          <span className="text-muted">
                            (
                            {flightValidation.flight.arrival.iata}
                            )
                          </span>
                        </div>
                      </div>
                      {/* Flight Times - Show departure or arrival based on transfer type */}
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          {/* For departure transfers, show departure time from origin */}
                          {flightValidation.transferType === 'departure' ? (
                            <>
                              <div>
                                <small className="text-muted">Departs: </small>
                                <span>
                                  {formatFlightTime(
                                    flightValidation.flight.departure.scheduled,
                                    flightValidation.flight.departure.timezone,
                                  )}
                                </span>
                              </div>
                              {flightValidation.flight.departure.estimated && (
                                <div>
                                  <small className="text-muted">Estimated: </small>
                                  <strong className="text-success">
                                    {formatFlightTime(
                                      flightValidation.flight.departure.estimated,
                                      flightValidation.flight.departure.timezone,
                                    )}
                                  </strong>
                                  <Badge bg="info" className="ms-2">Updated</Badge>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              {/* For arrival transfers, show arrival time at destination */}
                              <div>
                                <small className="text-muted">Scheduled: </small>
                                <span>
                                  {formatFlightTime(
                                    flightValidation.flight.arrival.scheduled,
                                    flightValidation.flight.arrival.timezone,
                                  )}
                                </span>
                              </div>
                              {flightValidation.flight.arrival.estimated && (
                                <div>
                                  <small className="text-muted">Estimated: </small>
                                  <strong className="text-success">
                                    {formatFlightTime(
                                      flightValidation.flight.arrival.estimated,
                                      flightValidation.flight.arrival.timezone,
                                    )}
                                  </strong>
                                  <Badge bg="info" className="ms-2">Updated</Badge>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {/* Show "Use as Pickup Time" for arrival transfers */}
                        {flightValidation.transferType !== 'departure'
                          && ['BUD', 'VIE', 'BTS', 'PRG', 'KRK', 'ZAG', 'LJU', 'DEB'].includes(
                            flightValidation.flight.arrival.iata,
                          ) && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={applyFlightArrivalTime}
                            title="Uses scheduled time"
                          >
                            <i className="fa fa-clock-o me-1" />
                            Use as Pickup Time
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Suggestions */}
                  {flightValidation.suggestions?.length > 0 && !flightValidation.found && (
                    <div className="text-muted small mt-1">
                      <i className="fa fa-info-circle me-1" />
                      {flightValidation.suggestions[0]}
                    </div>
                  )}
                </div>
                )}
              </Form.Group>
            </td>
          </tr>
          )}
          <tr>
            <td className="bg-body-secondary">Comments</td>
            <td>
              <Form.Group controlId="comments">
                <Form.Control
                  as="textarea"
                  value={order.comments}
                  onChange={handleChange}
                />
                <FormControlFeedback errors={validationErrors} field="comments" />
              </Form.Group>
            </td>
          </tr>
          <tr>
            <td className="bg-body-secondary">Admin Comments</td>
            <td>
              <Form.Group controlId="admin_comments">
                <Form.Control
                  as="textarea"
                  value={order.admin_comments}
                  onChange={handleChange}
                />
                <FormControlFeedback errors={validationErrors} field="admin_comments" />
              </Form.Group>
            </td>
          </tr>

          {/* Nobody travels on an expense. */}
          {!isExpense && (
          <>
            {/* Passengers */}
            <tr>
              <th className="bg-dark-subtle" colSpan={2}>Passengers</th>
            </tr>
            {capacityExceeded && (
            <tr>
              <td colSpan={2} className="p-0">
                <Alert variant="danger" className="mb-0 py-2">
                  <i className="fa fa-exclamation-triangle me-2" />
                  <strong>Capacity exceeded!</strong>
                  {' '}
                  {totalPassengers}
                  {' '}
                  passengers selected but
                  {' '}
                  {order.category.name}
                  {' '}
                  only has capacity for
                  {' '}
                  {order.category.capacity}
                  .
                </Alert>
              </td>
            </tr>
            )}
            <tr>
              <td className="bg-body-secondary">
                Total Amount
                <i className="fa fa-asterisk text-danger bg-body-secondary" />
              </td>
              <td>
                <Form.Group controlId="passengers.amount">
                  <Form.Control
                    className={capacityExceeded ? 'bg-danger-subtle' : 'bg-warning-subtle'}
                    type="number"
                    disabled
                    value={totalPassengers}
                  />
                  <FormControlFeedback errors={validationErrors} field="passengers.amount" />
                </Form.Group>
              </td>
            </tr>
            <tr>
              <td className="bg-body-secondary">
                Adults
                <i className="fa fa-asterisk text-danger bg-body-secondary" />
              </td>
              <td>
                <Form.Group controlId="passengers.adults">
                  <Form.Control
                    className="bg-warning-subtle"
                    type="number"
                    min={0}
                    value={Number(order.passengers.adults)}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="passengers.adults" />
                </Form.Group>
              </td>
            </tr>
            {order.booking_type === 'experience' ? (
              <tr>
                <td className="bg-body-secondary">Children</td>
                <td>
                  <Form.Group controlId="passengers.children">
                    <Form.Control
                      type="number"
                      min={0}
                      value={Number(order.passengers.children || 0)}
                      onChange={handleChange}
                    />
                    <FormControlFeedback errors={validationErrors} field="passengers.children" />
                  </Form.Group>
                </td>
              </tr>
            ) : (
              <>
                <tr>
                  <td className="bg-body-secondary">Convertible Seat</td>
                  <td>
                    <Form.Group controlId="passengers.convertible_seat">
                      <Form.Control
                        type="number"
                        min={0}
                        value={Number(order.passengers.convertible_seat)}
                        onChange={handleChange}
                      />
                      <FormControlFeedback errors={validationErrors} field="passengers.convertible_seat" />
                    </Form.Group>
                  </td>
                </tr>
                <tr>
                  <td className="bg-body-secondary">Booster Seat</td>
                  <td>
                    <Form.Group controlId="passengers.booster_seat">
                      <Form.Control
                        type="number"
                        min={0}
                        value={Number(order.passengers.booster_seat)}
                        onChange={handleChange}
                      />
                      <FormControlFeedback errors={validationErrors} field="passengers.booster_seat" />
                    </Form.Group>
                  </td>
                </tr>
                <tr>
                  <td className="bg-body-secondary">Infant Carrier</td>
                  <td>
                    <Form.Group controlId="passengers.infant_carrier">
                      <Form.Control
                        type="number"
                        min={0}
                        value={Number(order.passengers.infant_carrier)}
                        onChange={handleChange}
                      />
                      <FormControlFeedback errors={validationErrors} field="passengers.infant_carrier" />
                    </Form.Group>
                  </td>
                </tr>
              </>
            )}
          </>
          )}

          {/* Order Details */}
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>Order Details</th>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Order Status
              <i className="fa fa-asterisk text-danger bg-body-secondary" />
            </td>
            <td>
              <Form.Group controlId="status">
                {(() => {
                  // Lock the status field whenever the booking is in a
                  // system-managed state — covers both editing an existing
                  // Mongo Booking AND new Partner Sync offers prefilled with
                  // "Pending Ingestion". Without this, partner-sourced
                  // offers (KiwiTaxi, AtoB) would land with a blank status
                  // dropdown because their prefilled status isn't in the
                  // user-pickable allowlist below.
                  const lockField = isSystemManagedStatus(order.status);
                  return (
                    <>
                      <Form.Select
                        className="bg-warning-subtle"
                        value={order.status}
                        onChange={handleChange}
                        disabled={lockField}
                      >
                        {lockField ? (
                          <option value={order.status}>
                            {order.status}
                            {' — '}
                            {systemManagedStatusLabel(order.status)}
                          </option>
                        ) : (
                          <>
                            <option value="">Select order status</option>
                            {orderStatuses.map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </>
                        )}
                      </Form.Select>
                      <FormControlFeedback errors={validationErrors} field="status" />
                    </>
                  );
                })()}
              </Form.Group>
            </td>
          </tr>
          {order.status === 'Completed' && order.email_address && (
          <tr>
            <td className="bg-body-secondary">
              Review Email
            </td>
            <td>
              <Form.Check
                type="checkbox"
                id="skip_review_email"
                label="Skip review email for this customer"
                checked={order.skip_review_email || false}
                onChange={handleChange}
              />
            </td>
          </tr>
          )}
          {order.status === 'Cancelled' && (
          <>
            <tr>
              <td className="bg-body-secondary">
                Operator Cancellation Compensation
                <i className="fa fa-asterisk text-danger bg-body-secondary" />
              </td>
              <td>
                <Form.Group controlId="operator_cancellation_price">
                  <Form.Control
                    type="number"
                    value={order.operator_cancellation_price}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="operator_cancellation_price" />
                </Form.Group>
                <Form.Group controlId="operator_cancellation_currency">
                  <Form.Select
                    value={order.operator_cancellation_currency}
                    onChange={handleChange}
                  >
                    <option value="">Select currency</option>
                    <option>EUR</option>
                    <option>HUF</option>
                  </Form.Select>
                  <FormControlFeedback errors={validationErrors} field="operator_cancellation_currency" />
                </Form.Group>
              </td>
            </tr>
            <tr>
              <td className="bg-body-secondary">
                Partner Cancellation Compensation
                <i className="fa fa-asterisk text-danger bg-body-secondary" />
              </td>
              <td>
                <Form.Group controlId="partner_cancellation_price">
                  <Form.Control
                    type="number"
                    value={order.partner_cancellation_price}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="partner_cancellation_price" />
                </Form.Group>
                <Form.Group controlId="partner_cancellation_currency">
                  <Form.Select
                    value={order.partner_cancellation_currency}
                    onChange={handleChange}
                  >
                    <option value="">Select currency</option>
                    <option>EUR</option>
                    <option>HUF</option>
                  </Form.Select>
                  <FormControlFeedback errors={validationErrors} field="partner_cancellation_currency" />
                </Form.Group>
              </td>
            </tr>
          </>

          ) }
          {/* No vehicle is dispatched for a cost. */}
          {!isExpense && (
          <>
            {/* Vehicle Category - Only for transfers, hidden for experiences */}
            {order.booking_type !== 'experience' && (
            <>
              <tr>
                <td className="bg-body-secondary">
                  Vehicle Category
                  <i className="fa fa-asterisk text-danger bg-body-secondary" />
                </td>
                <td>
                  <Form.Group controlId="category.name">
                    <Typeahead
                      id="vehicle_category"
                      placeholder="Select a vehicle category"
                      options={vehicleCategories}
                      selected={selectedCategory}
                      onChange={setSelectedCategory}
                      positionFixed
                      inputProps={{ className: 'bg-warning-subtle' }}
                    />
                    <FormControlFeedback errors={validationErrors} field="category.name" />
                  </Form.Group>
                </td>
              </tr>
              <tr>
                <td className="bg-body-secondary">Capacity</td>
                <td>
                  <Form.Group controlId="category.capacity">
                    <Form.Control
                      type="number"
                      value={order.category.capacity}
                      onChange={handleChange}
                      disabled
                      className="bg-secondary-subtle"
                    />
                    <Form.Text className="text-muted">
                      Auto-set from vehicle category
                    </Form.Text>
                    <FormControlFeedback errors={validationErrors} field="category.capacity" />
                  </Form.Group>
                </td>
              </tr>
              <tr>
                <td className="bg-body-secondary">Max. Luggage</td>
                <td>
                  <Form.Group controlId="category.max_luggage">
                    <Form.Control
                      type="number"
                      value={order.category.max_luggage}
                      onChange={handleChange}
                      disabled
                      className="bg-secondary-subtle"
                    />
                    <Form.Text className="text-muted">
                      Auto-set from vehicle category
                    </Form.Text>
                    <FormControlFeedback errors={validationErrors} field="category.max_luggage" />
                  </Form.Group>
                </td>
              </tr>
            </>
            )}
          </>
          )}

          {/* THE COST — who is being paid and how much. On a trip this is a
              separate screen, because a booking is taken long before anybody
              has been found to run it. A cost record has it the other way
              round: the API refuses a new expense that names no operator or
              carries no amount, so asking for it anywhere but here means
              every save fails. Same component, same props as the Assign
              Operator modal — including the two-group picker and the guard
              that keeps a cost bucket out of the driver slot. */}
          {isExpense && (
          <>
            <tr>
              <th className="bg-dark-subtle" colSpan={2}>
                Cost
                <i className="fa fa-asterisk text-danger bg-dark-subtle" />
              </th>
            </tr>
            <tr>
              <td colSpan={2}>
                <Alert variant="secondary" className="py-2" data-testid="expense-operator-hint">
                  <i className="fa fa-receipt me-2" />
                  Add a line for each party the cost was paid to and put the
                  amount in Price.
                </Alert>
                <MultiOperatorAssignment
                  operators={order.operators || []}
                  onChange={(operators) => setOrder((prev) => ({ ...prev, operators }))}
                  showPricing
                  hideDriverRole
                  defaultCurrency={order.currency}
                />
                {/* The API's own sentence, shown before the request rather
                    than after it. `operators` is also the field the server
                    puts its refusal on, so a save that somehow gets past
                    this lands its message in the same place. */}
                {costGap && (
                  <div
                    className="alert alert-warning py-2 px-3 mt-2 mb-0 small"
                    data-testid="expense-cost-gap"
                  >
                    <i className="fa fa-triangle-exclamation me-2" />
                    {costGap}
                  </div>
                )}
                <FormControlFeedback errors={validationErrors} field="operators" />
              </td>
            </tr>
          </>
          )}

          {/* Payment Information. On an expense `price`/`original_price` are
              REVENUE, not the cost — the cost is the `operators[].price`
              above. They stay optional and visible because an expense may
              legitimately carry revenue when the cost is recharged, but they
              are relabelled so nobody types the cost into them, which is
              precisely what the fake bookings did. */}
          <tr>
            <th className="bg-dark-subtle" colSpan={2}>
              {isExpense ? 'Currency & Recharged Revenue' : 'Payment Information'}
            </th>
          </tr>
          {isExpense && (
          <tr>
            <td colSpan={2} className="p-0">
              <Alert variant="info" className="mb-0 py-2" data-testid="expense-amount-hint">
                <i className="fa fa-circle-info me-2" />
                <strong>The amount of this expense is the one on the cost line above.</strong>
                {' '}
                The fields below are revenue — fill them only if the cost is
                recharged.
              </Alert>
            </td>
          </tr>
          )}
          <tr>
            <td className="bg-body-secondary">
              {isExpense ? 'Recharged Revenue' : 'Base Price'}
              {!isExpense && <i className="fa fa-asterisk text-danger bg-body-secondary" />}
            </td>
            <td>
              <Form.Group controlId="original_price">
                <Form.Control
                  className="bg-warning-subtle"
                  type="number"
                  value={order.original_price}
                  onChange={(e) => {
                    setOrder({ ...order, original_price: e.target.value, price: e.target.value });
                    setPriceChangeNote(null); // Clear note when manually changed
                  }}
                />
                <FormControlFeedback errors={validationErrors} field="original_price" />
                {priceChangeNote && (
                  <Form.Text className="text-info d-block mt-1">
                    <i className="fa fa-info-circle me-1" />
                    Price updated:
                    {' '}
                    {priceChangeNote.from}
                    {' '}
                    →
                    {' '}
                    {priceChangeNote.to}
                    {' '}
                    {priceChangeNote.currency}
                  </Form.Text>
                )}
              </Form.Group>
            </td>
          </tr>
          {/* Final price is the discounted one, and an expense has no
              discount — the field would write a `discount.percentage` from a
              section that is not on screen. */}
          {!isExpense && (
          <tr>
            <td className="bg-body-secondary">Final Price (Discounted)</td>
            <td>
              <Form.Group controlId="price">
                <Form.Control
                  type="number"
                  value={order.price}
                  onChange={(e) => {
                    setOrder({
                      ...order,
                      price: e.target.value,
                      discount: {
                        ...order.discount,
                        percentage: Math.floor(
                          ((order.original_price - e.target.value) / order.original_price) * 100,
                        ),
                      },
                    });
                  }}
                />
                <FormControlFeedback errors={validationErrors} field="price" />
              </Form.Group>
            </td>
          </tr>
          )}
          <tr>
            <td className="bg-body-secondary">
              Currency
              <i className="fa fa-asterisk text-danger bg-body-secondary" />
            </td>
            <td>
              <Form.Group controlId="currency">
                <Form.Select
                  className="bg-warning-subtle"
                  value={order.currency}
                  onChange={handleChange}
                >
                  <option value="">Select currency</option>
                  <option>EUR</option>
                  <option>HUF</option>
                </Form.Select>
                <FormControlFeedback errors={validationErrors} field="currency" />
              </Form.Group>
            </td>
          </tr>
          <tr>
            <td className="bg-body-secondary">
              Type
              {!isExpense && <i className="fa fa-asterisk text-danger bg-body-secondary" />}
            </td>
            <td>
              <Form.Group controlId="payment.type">
                <Form.Select
                  className={hasOrphanCashPortion(order) ? 'bg-warning-subtle border-danger' : 'bg-warning-subtle'}
                  value={order.payment?.type}
                  onChange={handleChange}
                >
                  <option value="">Select a payment type</option>
                  {paymentTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Form.Select>
                <FormControlFeedback errors={validationErrors} field="payment.type" />
                {/* A cash portion is still recorded against the operator(s)
                    while the payment type says no cash was collected. Flag it
                    here — this is the field the user just changed — so it is
                    caught at the moment of the switch rather than surfacing
                    later as an unexplained operator balance. */}
                {hasOrphanCashPortion(order) && (
                  <div className="text-danger small mt-1" data-testid="orphan-cash-payment-warning">
                    <i className="fa fa-exclamation-triangle me-1" />
                    {`This booking still has a cash portion of ${cashPortionTotal(order)} recorded against the operator. `}
                    Clear it in Assign Operator, or set the payment type back to
                    Cash / Mixed Payment.
                  </div>
                )}
              </Form.Group>
            </td>
          </tr>
          <tr>
            <td className="bg-body-secondary">Paid</td>
            <td>
              <Form.Group controlId="payment.paid">
                <Form.Check
                  type="checkbox"
                  disabled={order.payment?.barion_payment_id}
                  checked={order.payment?.paid || order.payment?.barion_payment_id}
                  onChange={handleChange}
                />
              </Form.Group>
            </td>
          </tr>

          {/* A discount, an opt-in extra, a voucher and a customer billing
              address are all things sold to a PASSENGER. A cost record has
              none; the partner on it is who bears the cost, not a buyer. */}
          {!isExpense && (
          <>
            {/* Discount */}
            <tr>
              <th className="bg-dark-subtle" colSpan={2}>Discount</th>
            </tr>
            <tr>
              <td className="bg-body-secondary">Code</td>
              <td>
                <Form.Group controlId="discount.code">
                  <Form.Control
                    type="text"
                    value={order.discount.code}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="discount.code" />
                </Form.Group>
              </td>
            </tr>
            <tr>
              <td className="bg-body-secondary">Percentage</td>
              <td>
                <Form.Group controlId="discount.percentage">
                  <Form.Control
                    type="number"
                    disabled
                    value={order.discount.percentage}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="discount.percentage" />
                </Form.Group>
              </td>
            </tr>

            {/* Booking Extras — opt-in services from the admin-managed
              catalog. Available to any user with booking edit access
              (not gated on CanManageBookingServices; that perm is for
              editing the catalog itself). Server resolves prices from
              the catalog at save time, so the picker only needs to
              send service_id + quantity. */}
            <tr>
              <th className="bg-dark-subtle" colSpan={2}>Booking Extras</th>
            </tr>
            <tr>
              <td className="bg-body-secondary">Services</td>
              <td>
                <BookingServicesPicker
                  selectedServices={order.services || []}
                  onChange={(services) => setOrder({ ...order, services })}
                  passengerCount={order.passengers?.amount}
                  bookingCurrency={order.currency}
                />
              </td>
            </tr>

            {/* Voucher Information */}
            <tr>
              <th className="bg-dark-subtle" colSpan={2}>
                <div className="d-flex m-0 p-1 bg-dark-subtle">
                  <span>Voucher Information</span>
                </div>
              </th>
            </tr>
            <tr>
              <td className="bg-body-secondary">Important Information</td>
              <td>
                <Form.Group controlId="important_information">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={order.important_information || ''}
                    onChange={handleChange}
                    placeholder="Important information to display on the voucher (e.g., what to bring, dress code, meeting point details)"
                  />
                  <Form.Text className="text-muted">
                    Line breaks will be preserved on the voucher
                  </Form.Text>
                </Form.Group>
              </td>
            </tr>
            <tr>
              <td className="bg-body-secondary">Cancellation Policy</td>
              <td>
                <Form.Group controlId="cancellation_policy">
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={order.cancellation_policy || ''}
                    onChange={handleChange}
                    placeholder="Default: 24 hour cancellation policy"
                  />
                  <Form.Text className="text-muted">
                    If empty, voucher will show &quot;24 hour cancellation policy&quot;
                  </Form.Text>
                </Form.Group>
              </td>
            </tr>

            {/* Billing Information */}
            <tr>
              <th className="bg-dark-subtle" colSpan={2}>
                <div className="d-flex m-0 p-1 bg-dark-subtle">
                  <span>Billing Information</span>
                  <Button
                    disabled={!order.partner_id}
                    size="sm"
                    variant="secondary"
                    className="ms-auto"
                    onClick={() => setBillingDetailsFromPartner(order.partner_id)}
                  >
                    Override based on Partner
                    { ' ' }
                    <span className="fa fa-refresh" />
                  </Button>
                </div>

              </th>
            </tr>
            <tr>
              <td className="bg-body-secondary">Billing Name</td>
              <td>
                <Form.Group controlId="billing.name">
                  <Form.Control
                    type="text"
                    value={order.billing.name}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="billing.name" />
                </Form.Group>
              </td>
            </tr>
            <tr>
              <td className="bg-body-secondary">Billing Address</td>
              <td>
                <Form.Group controlId="billing.address">
                  <Form.Control
                    type="text"
                    value={order.billing.address}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="billing.address" />
                </Form.Group>
              </td>
            </tr>
            <tr>
              <td className="bg-body-secondary">Billing City</td>
              <td>
                <Form.Group controlId="billing.city">
                  <Form.Control
                    type="text"
                    value={order.billing.city}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="billing.city" />
                </Form.Group>
              </td>
            </tr>
            <tr>
              <td className="bg-body-secondary">Billing Postcode</td>
              <td>
                <Form.Group controlId="billing.postcode">
                  <Form.Control
                    type="text"
                    value={order.billing.postcode}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="billing.postcode" />
                </Form.Group>
              </td>
            </tr>
            <tr>
              <td className="bg-body-secondary">Billing Country</td>
              <td>
                <Form.Group controlId="billing.country">
                  <Form.Control
                    type="text"
                    value={order.billing.country}
                    onChange={handleChange}
                  />
                  <FormControlFeedback errors={validationErrors} field="billing.country" />
                </Form.Group>
              </td>
            </tr>
          </>
          )}

        </tbody>
      </Table>

      <div className="sticky-modal-footer">
        {/* A disabled button on its own is a dead end, so the reason is
            printed beside it. `costGap` is null on every trip, so this is
            the expense's requirement only. */}
        {costGap && (
          <span className="text-danger small me-auto" data-testid="expense-save-blocked">
            <i className="fa fa-triangle-exclamation me-1" />
            {costGap}
          </span>
        )}
        <Button
          onClick={handleSubmit}
          variant={showCoordWarning ? 'warning' : 'primary'}
          disabled={Boolean(costGap)}
          title={costGap || undefined}
        >
          {showCoordWarning ? 'Confirm Preview' : 'Preview'}
        </Button>
      </div>
    </>
  );
}

export default OrderInputForm;
